import { nextTick } from 'process';
import { PortalModule } from '@angular/cdk/portal';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { DialogService, FeedbackService, FlyoutService, LoadingService, NavigationService } from '@app/core/services';
import { ChildVendorUpdateModel, RelatedVendorModel, RelatedVendorUpdateModel, VendorSearchModel } from '@app/models/fund-management';
import { BaseFlyoutComponent, ModalComponent } from '@app/shared/components';
import { NgForTrackByIdDirective } from '@app/shared/directives/ng-for-track-by-id.directive';
import { RequiredNoWhitespaceDirective } from '@app/shared/directives/validators/required-no-whitespace.directive';
import { VendorService } from '../../services/vendor.service';

@Component({
	selector: 'fm-payable-related-vendors',
	templateUrl: './related-vendors.component.html',
	styleUrls: ['./related-vendors.component.less'],
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
		MatButtonModule,
		MatIconModule,
		NgIf,
		PortalModule,
		FormsModule,
		MatRadioModule,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		RequiredNoWhitespaceDirective,
		NgFor,
		NgForTrackByIdDirective,
		MatOptionModule,
		MatSlideToggleModule,
		MatTableModule,
		MatTooltipModule,
		AsyncPipe,
	],
})
export class RelatedVendorsComponent extends BaseFlyoutComponent implements OnInit, OnDestroy {
	@ViewChild('relatedVendorsForm') relatedVendorsForm: NgForm;
	@Input() vendorInfo: { entityId: number; isParent: boolean; parentId?: number; parentName: string; parentNameOnCheck: boolean };
	@Input() isReadOnly = false;
	@Output() relatedChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

	relationshipType: '' | 'Child' | 'Parent' = '';
	siblings: RelatedVendorModel[] = [];
	childrenDataSource: MatTableDataSource<RelatedVendorModel> = new MatTableDataSource<RelatedVendorModel>();
	model: RelatedVendorUpdateModel;
	filteredParentVendors: Observable<VendorSearchModel[]>;
	filteredChildVendors: Observable<VendorSearchModel[]>;

	private readonly destroy$ = new Subject();

	constructor(
		protected readonly dialog: MatDialog,
		protected readonly dialogService: DialogService,
		protected readonly navigationService: NavigationService,
		protected readonly flyoutService: FlyoutService,
		private readonly feedbackService: FeedbackService,
		private readonly loadingService: LoadingService,
		private readonly service: VendorService
	) {
		super(flyoutService, dialogService);
	}

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => !this.relatedVendorsForm?.dirty;

	ngOnInit(): void {
		this.flyoutService.flyout$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
			const isPortal = this.getPortal() === value;
			if (isPortal) {
				this.model = {
					parentVendorId: this.vendorInfo.parentId,
					parentName: this.vendorInfo.parentName,
					parentNameOnCheck: this.vendorInfo.parentNameOnCheck,
					children: [],
				} as RelatedVendorUpdateModel;

				if ((this.vendorInfo.isParent || this.vendorInfo.parentName) && this.vendorInfo.entityId > 0) {
					this.relationshipType = this.vendorInfo.isParent ? 'Parent' : this.vendorInfo.parentName ? 'Child' : '';
					if (this.relationshipType !== '') {
						this.onRelationshipTypeChange();
						this.loadData();
					}
				} else {
					this.relationshipType = '';
					this.siblings = [];
					this.childrenDataSource.data = [];
				}
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	onRelationshipTypeChange(): void {
		// Remove Clearing of the related vendors list when type is changed
		//this.childrenDataSource.data = [];

		if (this.relationshipType === 'Child') {
			nextTick(() => {
				this.filteredParentVendors = this.relatedVendorsForm?.controls.parentVendor.valueChanges.pipe(
					debounceTime(300),
					switchMap((searchText: string) => {
						this.loadingService.skipNextRequest();
						return this.service.search(searchText, 10, true, [this.vendorInfo.entityId], true);
					})
				);
			});
		} else if (this.relationshipType === 'Parent') {
			nextTick(() => {
				this.filteredChildVendors = this.relatedVendorsForm?.controls.childVendor.valueChanges.pipe(
					debounceTime(300),
					switchMap((searchText: string) => {
						this.loadingService.skipNextRequest();
						return this.service.search(
							searchText,
							10,
							false,
							[this.vendorInfo.entityId, ...this.childrenDataSource.data.map((item) => item.Id)],
							true,
							true
						);
					})
				);
			});
		}
	}

	onParentVendorBlur(): void {
		//Don't let them type random characters and then not pick a vendor from the dropdown
		if (!this.model.parentVendorId) {
			this.model.parentName = null;
		}
	}

	onParentVendorChanged(selectedOption: MatOption): void {
		if (selectedOption != null) {
			this.model.parentVendorId = parseInt(selectedOption.id, 10);
			this.model.parentName = selectedOption.value;
		} else {
			this.model.parentVendorId = null;
			this.model.parentName = null;
		}
	}

	onChildVendorSelected(selectedVendor: VendorSearchModel): void {
		this.childrenDataSource.data = [
			{
				Id: selectedVendor.Id,
				ShortName: selectedVendor.ShortName,
				Name: selectedVendor.VendorName,
				Active: selectedVendor.Active,
				ParentNameOnCheck: false,
			} as RelatedVendorModel,
			...this.childrenDataSource.data,
		];

		this.relatedVendorsForm.controls.childVendor.setValue(null);
	}

	onParentNameOnCheckToggle(row: RelatedVendorModel): void {
		row.ParentNameOnCheck = !row.ParentNameOnCheck;
	}

	removeChildVendor(id: number): void {
		this.childrenDataSource.data = this.childrenDataSource.data.filter((item) => item.Id !== id);
	}

	submit(): void {
		if (!this.relatedVendorsForm.valid || this.isReadOnly) {
			return;
		}

		if (this.vendorInfo.isParent && this.relationshipType !== 'Parent') {
			this.dialog
				.open(ModalComponent, {
					disableClose: true,
					data: {
						message: 'Changing the Relationship Type will remove the linked vendors, do you want to continue?',
					},
				})
				.afterClosed()
				.subscribe((result) => {
					if (result === true) {
						this.confirmSubmit();
					}
				});
		} else {
			this.confirmSubmit();
		}
	}

	private confirmSubmit(): void {
		if (this.relationshipType === '') {
			this.model.children = [];
			this.model.parentVendorId = null;
		} else if (this.relationshipType === 'Parent') {
			this.model.parentVendorId = null;
			this.model.children = this.childrenDataSource.data.map(
				(item) => ({ id: item.Id, parentNameOnCheck: item.ParentNameOnCheck } as ChildVendorUpdateModel)
			);
		}
		this.service.updateRelatedVendors(this.vendorInfo.entityId, this.model).subscribe(() => {
			this.relatedVendorsForm.form.markAsPristine();

			this.vendorInfo.parentName = this.model.parentName;
			this.vendorInfo.isParent = this.model.children.length > 0;
			this.feedbackService.success('updated', 'Related Vendors');
			this.close();

			this.relatedChanged.emit(true);
		});
	}

	private loadData(): void {
		this.service.getRelatedVendors(this.vendorInfo.entityId).subscribe((data: RelatedVendorModel[]) => {
			if (this.relationshipType === 'Child') {
				this.siblings = data;
			} else {
				this.childrenDataSource.data = data;
			}
		});
	}
}
